<script>
import { fetchStatistic } from '@apis/admin/statistic'
import { fetchAssets } from '@apis/admin/asset'
import map from 'lodash/map'
import find from 'lodash/find'
import fromPairs from 'lodash/fromPairs'
import strtotime from 'locutus/php/datetime/strtotime'

export default {
  data() {
    return {
      statistic: null,
      loading: false,
      search_params: {},
      assets: [],
      pickerOptions: {
        shortcuts: [
          {
            text: this.$t('statistic.shortcuts_today'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today') * 1000),
                new Date(strtotime('tomorrow') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_yesterday'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('yesterday') * 1000),
                new Date(strtotime('today') * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_thismonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of next month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_lastmonth'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(
                  strtotime('today', strtotime('first day of last month')) *
                    1000
                ),
                new Date(
                  strtotime('today', strtotime('first day of this month')) *
                    1000 -
                    1
                ),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_thisweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('this week')) * 1000),
                new Date(strtotime('today', strtotime('next week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_lastweek'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('today', strtotime('last week')) * 1000),
                new Date(strtotime('today', strtotime('this week')) * 1000 - 1),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_week'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-7 day') * 1000),
                new Date(),
              ])
            },
          },
          {
            text: this.$t('statistic.shortcuts_month'),
            onClick(picker) {
              picker.$emit('pick', [
                new Date(strtotime('-30 day') * 1000),
                new Date(),
              ])
            },
          },
        ],
      },
    }
  },
  computed: {
    c_statistic() {
      return fromPairs(
        map(this.assets, (asset) => {
          return [
            asset.seq,
            {
              direct_transfer: {
                ...find(this.statistic.direct_transfer, {
                  target_asset_seq: asset.seq,
                }),
              },
              deposit: {
                ...find(this.statistic.deposit, { asset_seq: asset.seq }),
              },
              withdraw: {
                ...find(this.statistic.withdraw, { asset_seq: asset.seq }),
              },
              private_application: {
                ...find(this.statistic.private_application, {
                  source_asset_seq: asset.seq,
                }),
              },
            },
          ]
        })
      )
    },
    c_date_range: {
      get() {
        const date_start = this.search_params.date_start || ''
        const date_end = this.search_params.date_end || ''
        return [
          date_start ? new Date(date_start * 1) : '',
          date_end ? new Date(date_end * 1) : '',
        ]
      },
      set([start, end]) {
        this.$set(
          this.search_params,
          'date_start',
          start ? `${start.getTime()}` : undefined
        )
        this.$set(
          this.search_params,
          'date_end',
          end ? `${end.getTime()}` : undefined
        )
      },
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.search_params = { ...this.$route.query }

      if (!this.$route.query.date_start || !this.$route.query.date_end) return

      this.loading = true
      Promise.all([this.loadStatistic(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadStatistic() {
      this.statistic = await fetchStatistic(this.$route.query)
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    handleDatePickerChange() {
      this.$router.push({ query: this.search_params })
    },
  },
}
</script>

<template>
  <div>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <td colspan="4">
              <el-date-picker
                v-model="c_date_range"
                :class="$style.fr"
                type="datetimerange"
                :picker-options="pickerOptions"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                align="right"
                @change="handleDatePickerChange"
              >
              </el-date-picker>
            </td>
          </tr>
        </tbody>
      </table>
    </form>
    <table v-if="!loading" :class="$style.colTable" cellspacing="0">
      <thead>
        <tr>
          <th scope="col" rowspan="2">
            <div :class="$style.backslash"
              ><div>{{ $t('statistic.category') }}</div
              >{{ $t('statistic.property') }}</div
            >
          </th>
          <th scope="col" rowspan="2">
            <div>{{ $t('statistic.direct') }}</div>
          </th>
          <th scope="col" colspan="2">
            <div>{{ $t('statistic.C2C') }}</div>
          </th>
          <th scope="col" rowspan="2">
            <div>{{ $t('statistic.deposit_amount') }}</div>
          </th>
          <th scope="col" rowspan="2">
            <div>{{ $t('statistic.withdrawal_amount') }}</div>
          </th>
          <th scope="col" rowspan="2">
            <div>{{ $t('statistic.withdrawal_fee') }}</div>
          </th>
        </tr>
        <tr>
          <th scope="col"
            ><div>{{ $t('statistic.announces_sale') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('statistic.purchasing_discloser') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(asset, idx) in assets"
          :key="asset.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td>{{ asset.title }} ({{ asset.code }})</td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template
              v-if="Object.keys(c_statistic[asset.seq].direct_transfer).length"
            >
              {{
                c_statistic[asset.seq].direct_transfer.fee_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template
              v-if="
                Object.keys(c_statistic[asset.seq].private_application).length
              "
            >
              {{
                c_statistic[asset.seq].private_application.deal_fee_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template
              v-if="
                Object.keys(c_statistic[asset.seq].private_application).length
              "
            >
              {{
                c_statistic[asset.seq].private_application.fee_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template v-if="Object.keys(c_statistic[asset.seq].deposit).length">
              {{
                c_statistic[asset.seq].deposit.request_amount_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template
              v-if="Object.keys(c_statistic[asset.seq].withdraw).length"
            >
              {{
                c_statistic[asset.seq].withdraw.request_amount_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
          <td :class="[$style.tRight, $style.nowrap]">
            <template
              v-if="Object.keys(c_statistic[asset.seq].withdraw).length"
            >
              {{
                c_statistic[asset.seq].withdraw.fee_sum
                  | trimDecimalZero
                  | numberFormat
              }}
            </template>
            <template v-else
              >-</template
            >
          </td>
        </tr>
        <tr v-if="!assets.length && !loading">
          <td colspan="7" :class="$style.tCenter">{{
            $t('statistic.msg_not_date')
          }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.colTable th,
.colTable td {
  vertical-align: middle;
}

.backslash {
  text-align: left !important;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="0" x2="100%" y2="100%" stroke="gray" /></svg>');
}
.backslash div {
  text-align: right !important;
}
td.nowrap {
  font-family: monospace;
  font-size: 14px;
  font-weight: bold;
}
</style>
